import { defineStore } from 'pinia';
import { useHotelsStore } from '@/store/hotels';
import { notify } from 'notiwind';

export const useAlertsStore = defineStore('alerts', {
    state: () => ({
        activeAlert: null,
        activeProperty: null,
        alertModalType: 'details',
        alerts: [],
        alertsViewFilters: [],
        showAlertModal: false,
        showDeleteAlertModal: false,
        error: null,
        groups: [],
        loading: true,
        loadingMatches: false,
        alertModalState: 'step1.create',
        selectedGroups: [],
        selectedProperties: [],
        selectedStatuses: [],
        // statuses: [],
        statusOptions: [
            { name: 'monitoring', label: 'Active' },
            { name: 'complete', label: 'Completed' },
            { name: 'expired', label: 'Expired' },
            { name: 'unavailable', label: 'Unavailable' },
            { name: 'found', label: 'Found' },
        ],
    }),
    getters: {
        selectedProperty() {
            if (this.selectedProperties.length === 1) {
                return this.alertProperties.find(
                    (p) => p.id === this.selectedProperties[0]
                );
            }
            return null;
        },
        groupsFilters(state) {
            state.alerts.forEach((item) => {
                if (
                    state.groups.findIndex(
                        (group) => item.property.group === group.slug
                    ) < 0
                ) {
                    state.groups.push({
                        image: item.property?.program?.image,
                        name: item.property?.program?.name,
                        slug: item.property.group,
                    });
                }
            });
            return state.groups;
        },
        statusFilters(state) {
            state.alerts.forEach((item) => {
                if (
                    state.statuses.findIndex(
                        (status) => item.status === status.name
                    ) < 0
                ) {
                    state.statuses.push({
                        name: item.status,
                        label: state.statusOptions.find(
                            (status) => status.name === item.status
                        )?.label,
                    });
                }
            });
            return state.statuses;
        },
        filteredAlerts(state) {
            return state.alerts.filter((alert) => {
                let group = true;
                let status = true;
                let property = true;

                if (state.selectedGroups.length > 0) {
                    group = state.selectedGroups.includes(alert.property.group);
                }

                if (state.selectedStatuses.length > 0) {
                    status = state.selectedStatuses.includes(alert.status);
                }

                if (state.selectedProperties.length > 0) {
                    property = state.selectedProperties.includes(
                        alert.property.id
                    );
                }

                return group && status && property;
            });
        },
        alertProperties(state) {
            return state.alerts.reduce((acc, alert) => {
                if (!acc.some((prop) => prop.id === alert.property.id)) {
                    acc.push(alert.property);
                }
                return acc;
            }, []);
        },
    },
    actions: {
        setAlertModalType(val) {
            this.alertModalType = val;
            //can be details, edit, create, delete
        },
        setActiveAlert(val) {
            this.activeAlert = val;
        },
        setActiveProperty(val) {
            this.activeProperty = val;
        },
        setAlertsViewFilters(val) {
            const index = this.alertsViewFilters.indexOf(val);
            if (index >= 0) {
                this.alertsViewFilters.splice(index, 1);
            } else {
                this.alertsViewFilters.push(val);
            }
        },
        setShowAlertModal(val) {
            this.showAlertModal = val;
        },
        setSelectedStatuses(val) {
            const index = this.selectedStatuses.indexOf(val);
            if (index >= 0) {
                this.selectedStatuses.splice(index, 1);
            } else {
                this.selectedStatuses.push(val);
            }
        },
        setSelectedProperties(val) {
            const index = this.selectedProperties.indexOf(val);
            if (index >= 0) {
                this.selectedProperties.splice(index, 1);
            } else {
                this.selectedProperties.push(val);
            }
        },
        replaceSelectedProperties(array) {
            this.selectedProperties = [...array];
        },
        setLoading(val) {
            this.loading = val;
        },
        setSelectedGroups(groupName) {
            const index = this.selectedGroups.indexOf(groupName);
            if (index >= 0) {
                this.selectedGroups.splice(index, 1);
            } else {
                this.selectedGroups.push(groupName);
            }
        },
        setSelectedStatuses(status) {
            const index = this.selectedStatuses.indexOf(status);
            if (index >= 0) {
                this.selectedStatuses.splice(index, 1);
            } else {
                this.selectedStatuses.push(status);
            }
        },
        selectAlert(a) {
            this.setActiveProperty(a.property);
            this.setActiveAlert(a);
        },
        async getAlerts(params = {}) {
            this.setLoading(true);
            try {
                const response = await ApiService.apiCall(
                    '/alerts',
                    'get',
                    params
                );
                if (response.status === 200) {
                    this.alerts = response.data.alerts;
                    return response.data.alerts;
                } else {
                    throw new Error(
                        `Failed to fetch alerts: ${response.status} error.`
                    );
                }
            } catch (error) {
                console.error(`Error getting alerts: ${error.message}`);
                this.alerts = [];
                this.error = 'Unable to retrieve your alerts.';
            } finally {
                this.setLoading(false);
            }
        },
        addAlert(alert) {
            const hotelsStore = useHotelsStore();
            hotelsStore.property?.user_alerts?.push(alert);
            this.alerts.push(alert);
        },
        uptAlert(alert) {
            const hotelsStore = useHotelsStore();
            let index = hotelsStore.property.user_alerts?.findIndex(
                (uAlert) => {
                    return uAlert.uuid === alert.uuid;
                }
            );
            if (index > -1) {
                hotelsStore.property.user_alerts[index] = alert;
            }

            let alts = this.alerts.map((uAlert) => {
                if (uAlert.uuid === alert.uuid) {
                    uAlert = alert;
                }
                return uAlert;
            });
            this.alerts = alts;
        },
        // This is for property specific alerts:
        deletePropertyAlert(alert) {
            const hotelsStore = useHotelsStore();
            let index = hotelsStore.property.user_alerts?.findIndex(
                (uAlert) => {
                    return uAlert.uuid === alert.uuid;
                }
            );
            if (index > -1) {
                hotelsStore.property.user_alerts.splice(index, 1);
            }
        },
        async deleteAlert(alert) {
            this.setLoading(true);

            ApiService.apiCall(`/alerts/${alert.uuid}`, 'delete')
                .then(() => {
                    this.getAlerts();
                    this.setLoading(false);
                    notify(
                        {
                            group: 'success',
                            title: 'Alert deleted',
                            text: 'Your hotel availability alert was deleted.',
                        },
                        4000
                    );
                })
                .catch((e) => {
                    this.setLoading(false);

                    notify(
                        {
                            group: 'error',
                            title: 'Problem deleting alert',
                            text: 'We could not delete the alert. Please try again later.',
                        },
                        4000
                    );
                });
        },

        async createAlert(data, propertyName = '') {
            ApiService.apiCall('/hotels/alerts', 'post', data)
                .then((r) => {
                    this.addAlert(r.data.data);
                    notify(
                        {
                            group: 'success',
                            title: 'Alert Created',
                            text: 'Availability alert has been successfully created.',
                        },
                        4000
                    );
                    if (import.meta.env.VITE_APP_ENV === 'production') {
                        woopra.track('property-alert', {
                            propertyname: propertyName,
                            id: data.property_id,
                            alerttype: data.alert_type,
                            startdate: data.check_in,
                            enddate: data.check_out,
                            numberofnights: data.number_of_nights,
                            maxpoints: data.max_points,
                            monthyear: data.month_year,
                            uuid: localStorage.getItem('uuid'),
                        });
                    }
                    this.getAlerts();
                })
                .catch((e) => {
                    console.error(e);
                    let message =
                        'There was a problem trying to create your alert.';

                    if (e.response?.data?.message) {
                        message = e.response?.data?.message;
                    }

                    errors.value.push({ message: message });
                });
        },

        async updateAlert(data) {
            ApiService.apiCall(`/alerts/${this.activeAlert.uuid}`, 'put', data)
                .then((r) => {
                    this.uptAlert(r.data.data);
                    notify(
                        {
                            group: 'success',
                            title: 'Alert Updated',
                            text: 'Availability alert has been successfully updated.',
                        },
                        4000
                    );
                    this.getAlerts();
                })
                .catch((e) => {
                    console.error(e);
                    let message =
                        'There was a problem trying to update your alert.';
                    if (e.response?.data?.message) {
                        message = e.response?.data?.message;
                    }
                    errors.value.push({ message });
                });
        },
        getMatches(alert) {
            this.loadingMatches = true;
            ApiService.apiCall(`/alerts/${alert.uuid}/matches`, 'get')
                .then((response) => {
                    alert = response.data;
                    this.loadingMatches = false;
                })
                .catch((e) => {
                    this.loadingMatches = false;
                });
        },
        clearSelectedGroups() {
            this.selectedGroups.splice(0);
        },
        clearSelectedProperties() {
            this.selectedProperties.splice(0);
        },
        clearSelectedStatuses() {
            this.selectedStatuses.splice(0);
            this.getAlerts();
        },
        clearFilters() {
            this.clearSelectedStatuses();
            this.clearSelectedGroups();
            this.clearSelectedProperties();
        },
        toggleSelectedGroup(option) {
            const index = this.selectedGroups.indexOf(option);
            if (index >= 0) {
                this.selectedGroups.splice(index, 1);
            } else {
                this.selectedGroups.push(option);
            }
        },
        async toggleComplete(alert) {
            alert.complete = !alert.complete;

            await ApiService.apiCall(`/alerts/${alert.uuid}/complete`, 'post', {
                complete: alert.complete,
            });
        },
        setShowDeleteAlertModal(val) {
            if (val) {
                this.setAlertModalState('step2.delete');
                this.setShowAlertModal(true);
            }
        },
        setAlertModalState(val) {
            this.alertModalState = val;
        },
        clearFilters() {
            this.clearSelectedStatuses();
            this.clearSelectedGroups();
            this.clearSelectedProperties();
        },
    },
});
